import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "@mui/material";
import Toast from "../../../components/MessageToast";
import DeleteConfirm from "../../../components/Dialogs/DeleteConfirm";
import ConfirmationDialog from "../../../components/Dialogs/ConfirmationDialog";
import FaqsList from "../../components/faq/FaqsList";
import CreateFaq from "./CreateFaq";
import { fetchFaqsData } from "../../../redux/faqs/index";
import actionCreator from "../../../redux/action/actionCreator";
import { DELETE_FAQ } from "../../../redux/faqs/types";
import {
  PUBLISH_OR_UNPUBLISH_CONTENT,
  RESET_PUBLISH_STORE,
} from "../../../redux/general/types";
import { DEFAULT_FAQ_PAGE } from "../../components/faq/faqOptions";

const INITIAL_DIALOGS_STATE = {
  formOpen: false,
  editingFaq: null,
  deleteTarget: null,
  publishTarget: null,
};

// No detail page, no separate create/edit route - FAQs are a handful of
// curated entries, not a resource that needs full-page navigation per item.
// Everything (list, add, edit, publish, delete) lives on this one page;
// add/edit opens the form in a dialog, publish/delete open a confirmation
// dialog, and every action just refetches this same list. Mirrors the
// Metrics management page (views/containers/metrics/index.js).
export default () => {
  const faqsData = useSelector((state) => state.faqsGlobalStore);
  const deleteStore = useSelector((state) => state.deleteFaqGlobalStore);
  const publishStore = useSelector((state) => state.publishGlobalStore);
  const dispatch = useDispatch();

  const [dialogs, setDialogs] = useState(INITIAL_DIALOGS_STATE);
  const [pendingToast, setPendingToast] = useState(null);

  const refetch = () => dispatch(fetchFaqsData(DEFAULT_FAQ_PAGE));

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const items = useMemo(() => faqsData.data?.data || [], [faqsData.data]);

  const openCreateDialog = () =>
    setDialogs({ ...INITIAL_DIALOGS_STATE, formOpen: true });

  const openEditDialog = (faq) =>
    setDialogs({ ...INITIAL_DIALOGS_STATE, formOpen: true, editingFaq: faq });

  const closeFormDialog = () =>
    setDialogs((prev) => ({ ...prev, formOpen: false, editingFaq: null }));

  const handleSaved = () => {
    closeFormDialog();
    refetch();
  };

  const openDeleteDialog = (faq) =>
    setDialogs((prev) => ({ ...prev, deleteTarget: faq }));

  const closeDeleteDialog = () =>
    setDialogs((prev) => ({ ...prev, deleteTarget: null }));

  const handleDeleteConfirm = async () => {
    const target = dialogs.deleteTarget;
    if (!target) return;

    await dispatch(
      actionCreator({
        method: "DELETE",
        path: `/api/faqs/${target.id}`,
        actionType: DELETE_FAQ,
      })
    );
    setDialogs((prev) => ({ ...prev, deleteTarget: null }));
    setPendingToast("delete");
  };

  const openPublishDialog = (faq) =>
    setDialogs((prev) => ({ ...prev, publishTarget: faq }));

  const closePublishDialog = () =>
    setDialogs((prev) => ({ ...prev, publishTarget: null }));

  const handlePublishConfirm = async () => {
    const target = dialogs.publishTarget;
    if (!target) return;

    const isUnpublish = target.status === "published";
    dispatch(actionCreator({ method: "", path: "", actionType: RESET_PUBLISH_STORE }));
    await dispatch(
      actionCreator({
        method: "PATCH",
        path: `/api/faqs/${target.id}`,
        actionType: PUBLISH_OR_UNPUBLISH_CONTENT,
        data: { status: isUnpublish ? "unpublished" : "published" },
      })
    );
    setDialogs((prev) => ({ ...prev, publishTarget: null }));
    setPendingToast("publish");
  };

  if (pendingToast === "delete" && deleteStore.message) {
    Toast({
      message: deleteStore.message,
      type: deleteStore.message.includes("successful") ? "success" : "error",
      onClose: refetch,
    });
    setPendingToast(null);
  }

  if (pendingToast === "publish" && publishStore.message) {
    Toast({
      message: publishStore.message,
      type: publishStore.message.includes("successful") ? "success" : "error",
      onClose: refetch,
    });
    setPendingToast(null);
  }

  const isUnpublishTarget = dialogs.publishTarget?.status === "published";

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        <Button
          variant="outlined"
          onClick={openCreateDialog}
          sx={{
            margin: "15px",
            color: "#E00186",
            border: "1px solid #E00186",
            fontFamily: "Arial",
            "&:hover": {
              color: "#fff",
              backgroundColor: "#E00186",
              border: "1px solid #E00186",
            },
          }}
        >
          Add FAQ
        </Button>
      </div>

      <FaqsList
        items={items}
        isLoading={faqsData.loading}
        onEdit={openEditDialog}
        onTogglePublish={openPublishDialog}
        onDelete={openDeleteDialog}
      />

      <CreateFaq
        open={dialogs.formOpen}
        faq={dialogs.editingFaq}
        defaultDisplayOrder={items.length}
        onClose={closeFormDialog}
        onSaved={handleSaved}
      />

      <DeleteConfirm
        open={Boolean(dialogs.deleteTarget)}
        dialogMessage="Do you want to delete this FAQ? Kindly note that this action is irreversible"
        onCancel={closeDeleteDialog}
        onConfirm={handleDeleteConfirm}
      />

      <ConfirmationDialog
        open={Boolean(dialogs.publishTarget)}
        title={isUnpublishTarget ? "Unpublish FAQ" : "Publish FAQ"}
        message={`Do you want to ${
          isUnpublishTarget ? "unpublish" : "publish"
        } this FAQ? Kindly note that this action might be irreversible`}
        confirmText={isUnpublishTarget ? "Unpublish" : "Publish"}
        onCancel={closePublishDialog}
        onConfirm={handlePublishConfirm}
      />
    </div>
  );
};
